import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Line } from "react-chartjs-2";

// Soft UI Dashboard React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Soft UI Dashboard React context
import { useMaterialUIController } from "context";

// Animation Plugin
const leftToRightRevealPlugin = {
    id: "leftToRightReveal",
    beforeInit(chart, args, options) {
        chart.$revealAnimation = {
            startTime: null,
            duration: options.duration || 2000,
            running: true,
        };
    },
    beforeDatasetsDraw(chart) {
        const chartArea = chart.chartArea;
        const ctx = chart.ctx;
        if (!chartArea || !ctx) return;
        const animationState = chart.$revealAnimation;
        if (!animationState.startTime) {
            animationState.startTime = performance.now();
        }
        const now = performance.now();
        const elapsed = now - animationState.startTime;
        const progress = Math.min(elapsed / animationState.duration, 1);
        const clipWidth = chartArea.left + (chartArea.right - chartArea.left) * progress;
        ctx.save();
        ctx.beginPath();
        // Start from x=0 and y=0 to ensure points on the edges (left/top/bottom) aren't clipped.
        // We only want to restrict the RIGHT edge, which is 'clipWidth'.
        ctx.rect(0, 0, clipWidth, chart.height);
        ctx.clip();
        if (progress < 1 && animationState.running !== false) {
            animationState.running = true;
            requestAnimationFrame(() => {
                if (chart.ctx) chart.update();
            });
        } else {
            animationState.running = false;
        }
    },
    afterDatasetsDraw(chart) {
        if (chart.ctx) chart.ctx.restore();
    },
};

// Chart configurations
function configs(labels, datasets) {
    return {
        data: {
            labels,
            datasets: [
                {
                    label: datasets.label,
                    tension: 0.4,
                    pointRadius: datasets.data.length < 2 ? 6 : 0,
                    pointBorderColor: datasets.data.length < 2 ? "#00A1A1" : "transparent",
                    pointBackgroundColor: datasets.data.length < 2 ? "#00A1A1" : "transparent",
                    borderColor: "#00A1A1",
                    borderWidth: 4,
                    backgroundColor: "#d1f2f2", // Semi-transparent pink fill
                    fill: true,
                    data: datasets.data,
                    maxBarThickness: 6,
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false, // Disable default Chart.js animation to let the plugin handle the reveal
            plugins: {
                legend: {
                    display: false,
                },
                leftToRightReveal: { duration: 2000 }, // Enable animation
            },
            interaction: {
                intersect: false,
                mode: "index",
            },
            scales: {
                y: {
                    grace: '25%',
                    grid: {
                        drawBorder: false,
                        display: true,
                        drawOnChartArea: true,
                        drawTicks: false,
                        borderDash: [5, 5],
                        color: "#c1c4ce5c",
                    },
                    ticks: {
                        display: true,
                        padding: 10,
                        color: "#9ca2b7",
                        font: {
                            size: 11,
                            family: "Roboto",
                            style: "normal",
                            lineHeight: 2,
                        },
                    },
                },
                x: {
                    grid: {
                        drawBorder: false,
                        display: false,
                        drawOnChartArea: true,
                        drawTicks: true,
                    },
                    ticks: {
                        display: true,
                        color: "#9ca2b7",
                        padding: 10,
                        font: {
                            size: 11,
                            family: "Roboto",
                            style: "normal",
                            lineHeight: 2,
                        },
                    },
                },
            },
        },
    };
}

function EntityGrowthChart({ title, description, chart }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const { data, options } = useMemo(() => configs(chart.labels || [], chart.datasets || {}), [chart]);

    // Register plugin manually if needed, or pass via Line component plugins prop
    // But standard react-chartjs-2 passes plugins via options if registered globally or local plugins prop. 
    // Let's pass it locally.
    const plugins = [leftToRightRevealPlugin];

    return (
        <MDBox p={2} bgColor={darkMode ? "dark" : "white"} borderRadius="xl" shadow="sm">
            <MDBox px={2} pb={2}>
                <MDBox mb={2}>
                    <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                        {title}
                    </MDTypography>
                    <MDTypography component="div" variant="button" color="text" fontWeight="regular">
                        {description}
                    </MDTypography>
                </MDBox>
                <MDBox height="12.5rem">
                    <Line data={data} options={options} plugins={plugins} />
                </MDBox>
            </MDBox>
        </MDBox>
    );
}

EntityGrowthChart.defaultProps = {
    title: "",
    description: "",
};

EntityGrowthChart.propTypes = {
    title: PropTypes.string,
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    chart: PropTypes.shape({
        labels: PropTypes.arrayOf(PropTypes.string),
        datasets: PropTypes.shape({
            label: PropTypes.string,
            data: PropTypes.arrayOf(PropTypes.number),
        }),
    }).isRequired,
};

export default EntityGrowthChart;
